import React from "react";
import { Loader2, Copy as CopyIcon } from "lucide-react";
import { toast } from "sonner";

export async function copyToClipboard(text, label = "Copied") {
  try {
    await navigator.clipboard.writeText(text ?? "");
    toast.success(label);
    return true;
  } catch {
    toast.error("Copy failed — your browser blocked clipboard access");
    return false;
  }
}

export function CopyBtn({ text, label = "Copied", title = "Copy", size = 13, className = "", testid }) {
  if (!text) return null;
  return (
    <button type="button" data-testid={testid} title={title}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); copyToClipboard(text, label); }}
      className={`inline-flex items-center transition-colors duration-200 ${className}`}
      style={{ color: "var(--si-text-2)" }}>
      <CopyIcon size={size} />
    </button>
  );
}


export function Loading({ full, label = "GATHERING INTELLIGENCE" }) {
  return (
    <div data-testid="loading-state" className={full ? "min-h-screen flex items-center justify-center" : "py-16 flex items-center justify-center"}
      style={{ background: full ? "var(--si-bg)" : "transparent" }}>
      <div className="font-mono text-sm" style={{ color: "var(--si-silver)" }}>
        {label}...<span className="blink">_</span>
      </div>
    </div>
  );
}

export function EmptyState({ icon: Icon, title, hint, action, testid = "empty-state" }) {
  return (
    <div data-testid={testid} className="flex flex-col items-center justify-center text-center py-16 px-6">
      {Icon && <Icon size={28} strokeWidth={1.5} style={{ color: "var(--si-primary)" }} className="mb-4" />}
      <div className="text-lg font-medium" style={{ color: "var(--si-text)" }}>{title}</div>
      {hint && <div className="text-sm mt-2 max-w-md" style={{ color: "var(--si-text-2)" }}>{hint}</div>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

const STATUS_COLORS = {
  confirmed: "var(--si-success)", verified_identity: "var(--si-success)",
  established_relationship: "var(--si-success)", communication_permitted: "var(--si-success)",
  uncertain: "var(--si-warning)", candidate: "var(--si-silver)", reported: "var(--si-silver)",
  documented_connection: "var(--si-teal-light)", inferred: "var(--si-primary)",
  disputed: "var(--si-danger)", outdated: "var(--si-danger)", ended: "var(--si-danger)",
  unverified: "var(--si-text-2)", active: "var(--si-success)", archived: "var(--si-text-2)",
  included: "var(--si-success)", excluded: "var(--si-danger)", pending: "var(--si-warning)",
};

export function Badge({ children, value, testid }) {
  const label = children != null && children !== "" ? children : value;
  const color = STATUS_COLORS[value] || "var(--si-silver)";
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-mono"
      style={{ border: `1px solid ${color}`, color, background: "transparent", borderRadius: 2 }}>
      <span style={{ width: 6, height: 6, background: color, display: "inline-block", borderRadius: 1 }} />
      {typeof label === "string" ? label.replace(/_/g, " ") : label}
    </span>
  );
}

export function Btn({ variant = "primary", className = "", children, ...props }) {
  const base = "inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium transition-colors duration-200 focus:outline-none focus:ring-2 disabled:opacity-40 disabled:pointer-events-none";
  const styles = {
    primary: { background: "var(--si-teal)", color: "#fff", border: "1px solid var(--si-teal)" },
    secondary: { background: "transparent", color: "var(--si-silver)", border: "1px solid var(--si-silver)" },
    ghost: { background: "transparent", color: "var(--si-text-2)", border: "1px solid transparent" },
    danger: { background: "transparent", color: "var(--si-danger)", border: "1px solid var(--si-danger)" },
  };
  return (
    <button {...props} className={`${base} ${className}`}
      style={{ borderRadius: 2, ...styles[variant], ...(props.style || {}) }}
      onMouseEnter={(e) => { if (variant === "primary") e.currentTarget.style.background = "var(--si-teal-light)"; else e.currentTarget.style.background = "var(--si-surface-hover)"; }}
      onMouseLeave={(e) => { if (variant === "primary") e.currentTarget.style.background = "var(--si-teal)"; else e.currentTarget.style.background = "transparent"; }}>
      {children}
    </button>
  );
}

export function Input(props) {
  return <input {...props} className={`w-full px-3 py-2 text-sm font-mono outline-none transition-colors duration-200 focus:ring-2 ${props.className || ""}`}
    style={{ background: "var(--si-bg)", color: "var(--si-text)", border: "1px solid var(--si-border)", borderRadius: 2, ...(props.style || {}) }} />;
}

export function Textarea(props) {
  return <textarea {...props} className={`w-full px-3 py-2 text-sm outline-none transition-colors duration-200 focus:ring-2 ${props.className || ""}`}
    style={{ background: "var(--si-bg)", color: "var(--si-text)", border: "1px solid var(--si-border)", borderRadius: 2, minHeight: 80, ...(props.style || {}) }} />;
}

export function Select({ children, ...props }) {
  return <select {...props} className={`w-full px-3 py-2 text-sm outline-none transition-colors duration-200 focus:ring-2 ${props.className || ""}`}
    style={{ background: "var(--si-bg)", color: "var(--si-text)", border: "1px solid var(--si-border)", borderRadius: 2, ...(props.style || {}) }}>
    {children}
  </select>;
}

export function Field({ label, children, hint, copyValue }) {
  return (
    <label className="block mb-4">
      <div className="si-label mb-1.5 flex items-center justify-between gap-2">
        <span>{label}</span>
        {copyValue ? <CopyBtn text={copyValue} label={`${label} copied`} testid={`copy-field`} /> : null}
      </div>
      {children}
      {hint && <div className="text-xs mt-1" style={{ color: "var(--si-text-2)" }}>{hint}</div>}
    </label>
  );
}

export function PageHeader({ title, sub, actions }) {
  return (
    <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
      <div>
        <h1 className="text-2xl font-medium tracking-tight" style={{ color: "var(--si-text)" }}>{title}</h1>
        {sub && <p className="text-sm mt-1" style={{ color: "var(--si-text-2)" }}>{sub}</p>}
      </div>
      {actions && <div className="flex gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function Panel({ children, className = "", style, ...rest }) {
  return <div {...rest} className={className} style={{ background: "var(--si-surface)", border: "1px solid var(--si-border)", borderRadius: 2, ...style }}>{children}</div>;
}

// Collapsible "Advanced" section — keeps rarely-used fields out of the way.
export function Collapsible({ label, children, defaultOpen = false, testid }) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="mb-4">
      <button type="button" data-testid={testid} onClick={() => setOpen((o) => !o)}
        className="si-label flex items-center gap-1.5" style={{ color: "var(--si-silver)" }}>
        <span style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform .15s", display: "inline-block" }}>▸</span>
        {label}
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
}

// Canonical phone/email editor. Reads/writes the entity `contact_methods` array
// ([{kind:'phone'|'email', value, label, source, verification_status}, ...]) so the
// data stays searchable and flows into Copy, PDFs, email Compose and backups.
export function ContactMethodsEditor({ value = [], onChange, idPrefix = "cm" }) {
  const mk = (c) => ({ value: c.value || "", label: c.label || "",
    source: c.source || "manual entry", verification_status: c.verification_status || "unverified" });
  const [phones, setPhones] = React.useState(() => {
    const p = (value || []).filter((c) => c.kind === "phone").map(mk);
    return p.length ? p : [{ value: "", label: "", source: "manual entry", verification_status: "unverified" }];
  });
  const [emails, setEmails] = React.useState(() => {
    const e = (value || []).filter((c) => c.kind === "email").map(mk);
    return e.length ? e : [{ value: "", label: "", source: "manual entry", verification_status: "unverified" }];
  });
  const others = React.useRef((value || []).filter((c) => c.kind !== "phone" && c.kind !== "email"));
  const emit = (ps, es) => {
    const cms = [
      ...ps.filter((p) => p.value.trim()).map((p) => ({ kind: "phone", value: p.value.trim(), label: p.label.trim(), source: p.source, verification_status: p.verification_status })),
      ...es.filter((e) => e.value.trim()).map((e) => ({ kind: "email", value: e.value.trim(), label: e.label.trim(), source: e.source, verification_status: e.verification_status })),
      ...others.current,
    ];
    onChange && onChange(cms);
  };
  const row = (list, setList, i, patch) => { const n = list.map((r, j) => (j === i ? { ...r, ...patch } : r)); setList(n); return n; };
  const renderRows = (kind, list, setList, placeholder, labelPh) => (
    <div className="mb-3">
      <div className="si-label mb-1.5">{kind === "phone" ? "Phone numbers" : "Email addresses"}</div>
      {list.map((r, i) => (
        <div key={i} className="flex gap-2 mb-2">
          <Input data-testid={`${idPrefix}-${kind}-value-${i}`} value={r.value} placeholder={placeholder}
            onChange={(e) => { const n = row(list, setList, i, { value: e.target.value }); kind === "phone" ? emit(n, emails) : emit(phones, n); }}
            style={{ flex: 2 }} />
          <Input data-testid={`${idPrefix}-${kind}-label-${i}`} value={r.label} placeholder={labelPh}
            onChange={(e) => { const n = row(list, setList, i, { label: e.target.value }); kind === "phone" ? emit(n, emails) : emit(phones, n); }}
            style={{ flex: 1 }} />
          <button type="button" data-testid={`${idPrefix}-${kind}-remove-${i}`} title="Remove"
            onClick={() => { const n = list.filter((_, j) => j !== i); const nn = n.length ? n : [{ value: "", label: "", source: "manual entry", verification_status: "unverified" }]; setList(nn); kind === "phone" ? emit(nn, emails) : emit(phones, nn); }}
            style={{ color: "var(--si-text-2)", padding: "0 6px" }}>×</button>
        </div>
      ))}
      <button type="button" data-testid={`${idPrefix}-${kind}-add`}
        onClick={() => { const n = [...list, { value: "", label: "", source: "manual entry", verification_status: "unverified" }]; setList(n); }}
        className="si-label" style={{ color: "var(--si-teal-light)" }}>+ Add {kind === "phone" ? "phone" : "email"}</button>
    </div>
  );
  return (
    <div>
      {renderRows("phone", phones, setPhones, "+1 555 123 4567", "label (e.g. mobile)")}
      {renderRows("email", emails, setEmails, "name@example.com", "label (e.g. work)")}
    </div>
  );
}
